<template>
  <Transition name="fly-from-bottom" appear>
    <div
      class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-white/90 dark:tw-bg-black/90"
    >
      <div
        data-testid="studio-confirmation-dialog"
        :class="`tw-m-2 tw-flex tw-max-h-full tw-min-w-[300px] tw-flex-col tw-rounded-xl tw-border tw-bg-white tw-shadow-2xl dark:tw-bg-black ${widthClass}`"
      >
        <div class="tw-relative tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden tw-px-4 tw-py-6">
          <div class="tw-flex tw-items-center">
            <template v-if="!hideIcon">
              <div v-if="type === 'error'" class="icon-circle tw-bg-red-50 dark:tw-bg-slate-900">
                <ExclamationTriangle class="tw-h-6 tw-w-6 tw-text-red-500" />
              </div>
              <div v-else-if="type === 'success'" class="icon-circle tw-bg-green-50 dark:tw-bg-slate-900">
                <Check class="tw-h-6 tw-w-6 tw-text-green-500" />
              </div>
              <div v-else-if="type === 'lock'" class="icon-circle tw-bg-deep-orange-600 dark:tw-bg-slate-900">
                <TablerLock class="tw-h-6 tw-w-6 tw-text-white" />
              </div>
              <div v-else-if="type === 'edit'" class="icon-circle tw-bg-accent">
                <IconEdit class="tw-h-6 tw-w-6" />
              </div>
              <div v-else class="icon-circle tw-bg-deep-orange-50 dark:tw-bg-slate-900">
                <ExclamationTriangle class="tw-h-6 tw-w-6 tw-text-deep-orange-500" />
              </div>
            </template>
            <div :class="['tw-pl-4', { 'tw-pl-0': hideIcon }]">
              <h5
                class="tw-text-md tw-mb-1 tw-font-medium tw-leading-tight tw-text-black dark:tw-text-white lg:tw-text-lg lg:tw-leading-tight"
              >
                {{ heading }}
              </h5>
              <p class="!tw-mb-0 tw-text-sm tw-text-slate-500" v-html="message"></p>
            </div>
          </div>
        </div>
        <slot />
        <div v-if="showFooter" class="tw-item-center tw-flex tw-justify-end tw-border-t tw-p-2">
          <StudioButton
            v-if="secondaryActionTitle"
            trackingId="confirmation-dialog-secondary-action"
            variant="outline"
            :disabled="actionsDisabled"
            @click="$emit('secondaryAction')"
          >
            {{ secondaryActionTitle }}
          </StudioButton>
          <StudioButton
            v-if="primaryActionTitle"
            trackingId="confirmation-dialog-primary-action"
            variant="filled"
            type="submit"
            class="tw-ml-2"
            :disabled="actionsDisabled || isPrimaryActionButtonDisabled"
            :disabledReason="primaryActionDisabledReason"
            @click="$emit('primaryAction')"
          >
            {{ primaryActionTitle }}
          </StudioButton>
          <slot name="confirmation-dialog-additional-ctas" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts">
import { IconEdit } from '@tabler/icons-vue';
import { defineComponent } from 'vue';

import { ExclamationTriangle, Check } from './Icons';
import StudioButton from './StudioButton.vue';

import TablerLock from '@/studioComponents/Icons/tabler/TablerLock.vue';

export default defineComponent({
  name: 'StudioConfirmationDialog',
  components: {
    StudioButton,
    ExclamationTriangle,
    Check,
    TablerLock,
    IconEdit
  },
  props: {
    heading: {
      type: String,
      required: true
    },
    message: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: ''
    },
    primaryActionTitle: {
      type: String,
      default: ''
    },
    secondaryActionTitle: {
      type: String,
      default: ''
    },
    actionsDisabled: {
      type: Boolean,
      default: false
    },
    widthClass: {
      type: String,
      default: 'tw-max-w-md'
    },
    isPrimaryActionButtonDisabled: {
      type: Boolean,
      default: false
    },
    primaryActionDisabledReason: {
      type: String,
      default: ''
    },
    hideIcon: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    showFooter(): boolean {
      if (this.primaryActionTitle || this.secondaryActionTitle) {
        return true;
      }

      return false;
    }
  }
});
</script>

<style lang="scss" scoped>
.fly-from-bottom-enter-active,
.fly-from-bottom-leave-active {
  transition:
    opacity 0.25s,
    transform 0.25s;
}
.fly-from-bottom-enter-from,
.fly-from-bottom-leave-to {
  opacity: 0;
  transform: translateY(100px);
}
.icon-circle {
  display: flex;
  height: 2.5rem;
  width: 2.5rem;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
}
</style>
